import { Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../auth/useAuth";
import { Language, useI18n } from "../i18n";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

function titleFromPath(pathname: string, t: (key: string) => string) {
  if (pathname.startsWith("/events/") || pathname.startsWith("/event/")) return t("nav.actionDetails");
  if (pathname.startsWith("/events")) return t("nav.actions");
  if (pathname.startsWith("/my-events")) return t("nav.myActions");
  if (pathname.startsWith("/race")) return t("nav.liveBrevet");
  if (pathname.startsWith("/track")) return t("nav.publicTracking");
  if (pathname.startsWith("/routes")) return t("nav.myRoutes");
  if (pathname.startsWith("/route-editor")) return t("nav.routeEditorFull");
  if (pathname.startsWith("/community/routes")) return t("nav.communityRoutes");
  if (pathname.startsWith("/community/segments")) return t("nav.segments");
  if (pathname.startsWith("/segments") || pathname.startsWith("/segment-leaderboards")) return t("nav.segmentRankings");
  if (pathname.startsWith("/actions")) return t("nav.tasks");
  if (pathname.startsWith("/admin")) return t("nav.admin");
  if (pathname.startsWith("/results")) return t("nav.results");
  if (pathname.startsWith("/profile")) return t("nav.profile");
  return t("nav.dashboard");
}

function isPublicShellPath(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/dashboard" ||
    pathname === "/events" ||
    pathname.startsWith("/event/") ||
    pathname === "/event.html" ||
    pathname.startsWith("/race/") ||
    pathname === "/race.html" ||
    pathname.startsWith("/track/") ||
    pathname === "/track.html"
  );
}

export function AppShell() {
  const location = useLocation();
  const { user, loading, signIn } = useAuth();
  const { currentLanguage, setLanguage, supportedLanguages, t } = useI18n();
  const [signingIn, setSigningIn] = useState(false);
  const [signInFailed, setSignInFailed] = useState(false);
  const title = titleFromPath(location.pathname, t);

  async function handleSignIn() {
    setSigningIn(true);
    setSignInFailed(false);

    try {
      await signIn();
    } catch {
      setSignInFailed(true);
    } finally {
      setSigningIn(false);
    }
  }

  if ((loading || !user) && isPublicShellPath(location.pathname)) {
    return (
      <div className="public-app-shell">
        <main className="shell-main public-shell-main">
          <TopBar title={title} />
          <div className="content-area">
            <Outlet />
          </div>
        </main>
      </div>
    );
  }

  if (loading || !user) {
    return (
      <main className="auth-gate">
        <section className="auth-card">
          <img src="/logo.png" alt="" />
          <div>
            <label htmlFor="login-language">{t("language.label")}</label>
            <select
              id="login-language"
              value={currentLanguage}
              onChange={(event) => setLanguage(event.target.value as Language)}
            >
              {supportedLanguages.map((language) => (
                <option key={language} value={language}>
                  {t(`language.${language}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <h1>{t("auth.title")}</h1>
            <p>{loading ? t("auth.loading") : t("auth.body")}</p>
          </div>
          {signInFailed ? <p className="auth-error">{t("auth.error")}</p> : null}
          <button type="button" disabled={loading || signingIn} onClick={handleSignIn}>
            {signingIn ? t("auth.signingIn") : t("common.signInGoogle")}
          </button>
          <nav className="auth-links" aria-label="PULS public documents">
            <a href="/policy.html">{t("auth.privacy")}</a>
            <a href="/account-deletion.html">{t("auth.deleteAccount")}</a>
          </nav>
        </section>
      </main>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="shell-main">
        <TopBar title={title} />
        <div className="content-area">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
